import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_URL } from './constants';

// Create an axios instance
const instance = axios.create({
  withCredentials: true, // Ensures cookies are sent with each request
  baseURL: `${API_URL}/`
});

// Define a flag to prevent multiple refresh token calls
let isRefreshing = false;
let failedQueue: {
  resolve: (value?: AxiosResponse | PromiseLike<AxiosResponse> | undefined) => void;
  reject: (reason?: any) => void;
}[] = [];

// Helper function to process queued requests
const processQueue = (error: any, response: AxiosResponse | null = null): void => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(response!);
    }
  });
  failedQueue = [];
};

// Response interceptor for handling token expiration
instance.interceptors.response.use(
  (response: AxiosResponse) => response, // Pass through successful responses
  (error: AxiosError): Promise<AxiosResponse> => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Check for 401 status and ensure the request hasn't been retried
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        // If a refresh is already in progress, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((tokenResponse) => instance(originalRequest)) // Retry the original request
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true; // Mark the request as retried
      isRefreshing = true;

      return axios
        .get(`${API_URL}/rest/api/user/refresh/token`, {
          withCredentials: true // Ensure cookies are sent
        })
        .then((response) => {
          // Process the queued requests
          processQueue(null, response);

          // Retry the original request
          return instance(originalRequest);
        })
        .catch((refreshError) => {
          processQueue(refreshError, null);

          // Optional: Handle logout or redirect logic here
          console.error('Token refresh failed, logging out the user');
          return Promise.reject(refreshError);
        })
        .finally(() => {
          isRefreshing = false;
        });
    }

    return Promise.reject(error);
  }
);

export { instance as axios };
